import * as _ from 'lodash';
import { Component, OnInit } from '@angular/core';
import { Router } from '@angular/router';
import { PropertiesService } from 'src/app/services/properties.service';
import { SurveyRadioTableAnswer, SurveyQuestion, Survey, SurveyTextareaAnswer } from 'src/app/models/survey';
import { ClientProperty, UserSurvey } from 'src/app/models';
import * as moment from 'moment';
import { AuthService } from 'src/app/services/auth.service';

type UIClientProperty = ClientProperty & {
  moveInInspectionChecklist?:          Survey;
  moveInInspectionChecklistCompleted?: UserSurvey;
};

type UIComment = {
   show: boolean;
   text: string;
};

@Component({
  selector: 'app-condition-checklist-rw',
  templateUrl: './condition-checklist-rw.component.html',
  styleUrls: ['./condition-checklist-rw.component.css']
})
export class ConditionChecklistRwComponent implements OnInit {
  minDateMovedIn = "2000-01-01"
  maxDateMovedIn = moment(new Date()).format('YYYY-MM-DD');

  data = {
    loaded:           false,
    readonly:         false,
    changed:          false,
    dateMovedIn:      null as Date|null,
    formCompleted:    null as Date|null, // readonly
    checklist:        []   as SurveyQuestion<SurveyRadioTableAnswer>[],
    ackSmokeDetector: false,
  };

  previewByAdmin = false;

  readonly options = [
    { value: '1', label: '1 - Poor'      },
    { value: '2', label: '2 - Worn'      },
    { value: '3', label: '3 - Average'   },
    { value: '4', label: '4 - Good'      },
    { value: '5', label: '5 - Excellent' },
  ];

  question1: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Front door and entry',
    answer: {
      type: 'radio_table',
      questions: [
        'Front door and entry',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question2: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Living room',
    answer: {
      type: 'radio_table',
      questions: [
        'Living room',
        'Floor',
        'Ceiling',
        'Walls & windows',
        'Fixtures & closets',
        'Other',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question3: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Kitchen',
    answer: {
      type: 'radio_table',
      questions: [
          'Kitchen',
          'Floor',
          'Ceiling & lights',
          'Walls & windows',
          'Refrigerator',
          'Range/stove',
          'Woodwork-cabinets & counters',
          'Sink & plumbing',
          'Other',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question4: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Bathroom',
    answer: {
      type: 'radio_table',
      questions: [
          'Bathroom',
          'Floor',
          'Ceiling & lights',
          'Walls & fixtures',
          'Shower/tub',
          'Sink & counter',
          'Toilet',
          'Other',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question5: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Bedroom',
    answer: {
      type: 'radio_table',
      questions: [
          'Bedroom',
          'Floor',
          'Ceiling & lights',
          'Walls & windows',
          'Doors, closets & woodwork',
          'Other',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  question6: SurveyQuestion<SurveyRadioTableAnswer> = {
    question: 'Other',
    answer: {
      type: 'radio_table',
      questions: [
          'Other',
      ],
      options: this.options.map(o => o.label),
      answers: [] as string[],
      comments: [] as UIComment[] as any[]
    }
  };

  property!: UIClientProperty;

  submitting = false;

  constructor(private propertiesService: PropertiesService, private router: Router, private auth: AuthService) {}

  ngOnInit(): void {
    this.previewByAdmin = this.auth.isAdmin;
    this.data.checklist = this.questions;
    this.loadProperty();
  }

  get questions() {
    return [this.question1, this.question2, this.question3, this.question4, this.question5, this.question6];
  }

  ui = {
    comment: (v: unknown) => v as UIComment
  };

  private loadProperty() {
    this.property = this.propertiesService.currentProperty;

    if (!this.property) {
      this.router.navigate(['/courses/RentWise/properties']);
      return;
    }

    this.initForm();
  }

  private clone<T extends object>(obj: T|undefined) {
    return obj ? JSON.parse(JSON.stringify(obj)) as T : obj;
  }

  private initForm() {
    let survey = this.property.moveInInspectionChecklist;

    survey = this.clone(survey);

    // load questions from database
    if (survey) {
      this.data.readonly = false;

      survey.questions.forEach((q, i) => {
        this.data.checklist[i] = q as SurveyQuestion<SurveyRadioTableAnswer>;
      });
    }

    // present each bedroom on the list, put its number at the end
    if (Number.isFinite(this.property.bedrooms) && this.property.bedrooms !== 1) {
      const count = Math.max(0, this.property.bedrooms);
      const item = this.data.checklist.find(q => q.question === 'Bedroom');
      const index = item ? this.data.checklist.indexOf(item) : -1;
      const bedrooms = Array(count).fill(item).map((bedroom, index) => {
        const newInstance = JSON.parse(JSON.stringify(bedroom)) as SurveyQuestion<SurveyRadioTableAnswer>;
        const number = ' ' + (index + 1);
        newInstance.question = newInstance.question + number;
        newInstance.answer.questions[0] = newInstance.answer.questions[0] + number;
        return newInstance;
      });
      this.data.checklist.splice(index, 1, ...bedrooms);
    }

    // present each bathroom on the list, put its number at the end
    if (Number.isFinite(this.property.bathrooms) && this.property.bathrooms !== 1) {
      const count = Math.max(0, this.property.bathrooms);
      const item = this.data.checklist.find(q => q.question === 'Bathroom');
      const index = item ? this.data.checklist.indexOf(item) : -1;
      const bathrooms = Array(count).fill(item).map((bathroom, index) => {
        const newInstance = JSON.parse(JSON.stringify(bathroom)) as SurveyQuestion<SurveyRadioTableAnswer>;
        const number = ' ' + (index + 1);
        newInstance.question = newInstance.question + number;
        newInstance.answer.questions[0] = newInstance.answer.questions[0] + number;
        return newInstance;
      });
      this.data.checklist.splice(index, 1, ...bathrooms);
    }

    let completed = this.property.moveInInspectionChecklistCompleted;

    completed = this.clone(completed);

    // fill in answers from completed form
    if (completed) {
      this.data.readonly = true;
      this.data.formCompleted = completed.completionDate || null;

      // let table = -1;

      completed.data.forEach(q => {
        if (q.answer.type === 'textarea' && q.question === 'Date Moved In') {
          if (q.answer.text) {
            this.data.dateMovedIn = moment(q.answer.text).toDate();
          }
        }

        if (q.answer.type === 'radio_table') {
          const table = this.data.checklist.findIndex(data => data.answer.type === 'radio_table' && data.questionID === q.questionID && data.question === q.question);

          if (this.data.checklist[table]) {
            this.data.checklist[table] = q as SurveyQuestion<SurveyRadioTableAnswer>;
          }
        }

        if (q.answer.type === 'textarea' && q.question === 'Acknowledge smoke detector present') {
          if (q.answer.text) {
            this.data.ackSmokeDetector = q.answer.text === 'Acknowledged';
          }
        }
      });
    }

    // init answers and comments
    this.data.checklist.forEach((q, i) => {
      q.questionNumber = i + 1;

      if (q.answer.type === 'radio_table') {
        q.answer.answers  = q.answer.questions.map((a, i) => q.answer.answers[i] || '');
        q.answer.comments = q.answer.questions.map((a, i) => ({
          show: Boolean(q.answer.comments[i] || ''),
          text: q.answer.comments[i] || '',
        }) as UIComment) as any[];
      }
    });

    if (this.previewByAdmin) {
      this.data.readonly = true;
    }

    this.data.loaded = true;
  }

  table_radioGroup_keypress(answer: SurveyRadioTableAnswer, option: number, event: KeyboardEvent) {
    if (answer.options.some(opt => opt.startsWith(event.key))) {
      answer.answers[option] = event.key;
      this.data.changed = true;
    }
  }

  async table_radio_click(answer: SurveyRadioTableAnswer, value: string, option: number, event: MouseEvent) {
    if (answer.answers[option] === value) {
      answer.answers[option] = ''; // reset
      (event.target as any)?.blur();
    }
    else {
      answer.answers[option] = value;
    }

    this.data.changed = true;

    // select this option for all answers / radio groups when you click 3 times in a row (1st row only) - trick for debugging
    if (option === 0 && event.detail === 3) {
      this.data.checklist.forEach(q => {
        if (q.answer.type === 'radio_table') {
          this.setAll(q.answer, value);
        }
      });
    }
  }

  private setAll(answer: SurveyRadioTableAnswer, value: string) {
    answer.questions.forEach((q, i) => {
      answer.answers[i] = value;
    });
  }

  table_toggleComment(question: SurveyQuestion<SurveyRadioTableAnswer>, option: number) {
    const comment = this.ui.comment(question.answer.comments[option]);
    comment.show = !comment.show;
  }

  async submitForm() {
    if (this.data.readonly) {
      return;
    }

    if (this.submitting) {
      return;
    }

    this.submitting = true;

    try {
      const propertyID = this.property._id;
      const userID     = this.property.userID;
      const data       = this.prepareData();

      await this.propertiesService.sendMoveInInspectionForm(propertyID, userID, data).toPromise();

      this.propertiesService.properties = []; // reset cache

      await this.router.navigate(['/courses/RentWise/properties']);
    } catch (err) {
      this.submitting = false;
      throw err;
    }
  }

  private prepareData() {
    const dateMovedIn: SurveyQuestion<SurveyTextareaAnswer> = {
      question: 'Date Moved In',
      answer: {
        type: 'textarea',
        text: this.data.dateMovedIn ? moment(this.data.dateMovedIn).format('YYYY-MM-DD') : '',
      }
    };

    const checklist = this.data.checklist.map(q => {
      let question = { ...q };

      if (question.answer.type === 'radio_table') {
        const uiComments = question.answer.comments.map(this.ui.comment);

        question.answer.comments = uiComments.map(c => c.show ? c.text : '');
      }

      return question;
    });

    const ackSmokeDetector: SurveyQuestion<SurveyTextareaAnswer> = {
      question: 'Acknowledge smoke detector present',
      answer: {
        type: 'textarea',
        text: this.data.ackSmokeDetector ? 'Acknowledged' : '',
      }
    };

    const form = [ dateMovedIn, ...checklist, ackSmokeDetector ];
    return form;
  }

  goBack() {
    if (this.previewByAdmin) {
      this.router.navigate(['/admin/client-properties']);
      return;
    }

    if (this.data.changed && confirm('You have unsaved changes. Discard them and go back?') !== true) {
      return;
    }

    this.router.navigate(['/courses/RentWise/properties']);
  }
}
